import React from "react";
import { Helmet } from "react-helmet-async";
import { BASE_URL } from "../config";


/**
 * Reusable SEO head component that sets title, meta description,
 * Open Graph, Twitter Cards, canonical URL, and optional JSON-LD.
 *
 * @param {Object} props
 * @param {string} props.title - Page title (50-60 chars ideal)
 * @param {string} props.description - Meta description (150-160 chars ideal)
 * @param {string} props.path - URL path (e.g. "/resources/shoulder-range-of-motion")
 * @param {string} [props.ogImage] - Open Graph image URL
 * @param {string} [props.ogType] - Open Graph type (default "website")
 * @param {Object|Array} [props.jsonLd] - JSON-LD structured data object(s)
 * @param {boolean} [props.noindex] - Set true to prevent indexing
 */
function SEOHead({
  title,
  description,
  path = "/",
  ogImage = `${BASE_URL}/logo512.png`,
  ogType = "website",
  jsonLd = null,
  noindex = false,
}) {
  const canonicalUrl = `${BASE_URL}${path}`;
  const fullTitle = title.includes("WeGuide")
    ? title
    : `${title} | WeGuide Movement Analysis`;

  return (
    <Helmet>
      {/* Core meta */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="WeGuide Movement Analysis" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

export default SEOHead;
